import {
  Cake as CakeIcon,
  ContactEmergency,
  Email as EmailIcon,
  Event as EventIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
  PractitionerWrapper,
  WrappedPractitioner,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import {
  PractitionerRoleWrapper,
  WrappedPractitionerRole,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { PatientManager } from 'src/@types/crs/patient';
import { PaginateQueryWithStats } from 'src/api/pagination/dtos';
import { fhirClient } from 'src/App';
import FilterDrawer from 'src/components/FilterDrawer';
import Iconify from 'src/components/Iconify';
import { TableHeadCustom } from 'src/components/table';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useNotiMessage from 'src/hooks/useNotiMessage';
import useObjectState from 'src/hooks/useObjectState';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import {
  Bundle,
  Identifier,
  Practitioner,
  PractitionerRole,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import { getReferenceId, identifierByConfig } from 'src/utils/fhir';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { ACLValidationMode, checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { debounce, wait } from 'src/utils/timers';
import LoadingTableRow from '../../../../components/table/LoadingTableRow';
import { PaginatedPatientDto, PatientService } from '../../../../crs/patient/services/PatientService';
import useTable from '../../../../hooks/useTable';
import { TableToolbar } from 'src/careflow/common';
import NoResultsTableRow from 'src/careflow/common/NoResultsTableRow';
import { TABLE_HEAD_PATIENT_LIST } from 'src/careflow/common/table-head';
import AddPatientModal from '../detail/components/AddPatientModal';
import { getUserOrganizations } from 'src/careflow/ph-core/admin/permissions/util/get.user.permissions';
import { getOrganizationsByIds } from 'src/careflow/ph-core/admin/organization/service/organizations';
import { PatientDto } from 'src/crs/patient/dto/patient.dto';
import PatientRow from './PatientRow';
import { PatientLayout } from '../detail/components/PatientLayout';
import { GeneralPractitionerGrid } from 'src/careflow/helpers/types';
import { translateTableHead } from 'src/careflow/helpers/common';

export const PatientHeaderColumn = (showMedicaID: boolean) => {
  const MedicaIdColumn = showMedicaID
    ? {
        headerColumn: 'id',
        value: 'indexedCustomIdentifierMedicaidId',
        label: 'Medicaid ID',
        icon: (
          <ContactEmergency
            sx={{ marginRight: 1, color: '#919eab', marginBottom: '-3px' }}
            fontSize={'small'}
          />
        ),
      }
    : {};

  return [
    {
      headerColumn: 'id',
      value: 'indexedMrn',
      label: 'MRN',
      icon: (
        <Iconify
          icon="mdi:letter-m"
          sx={{ fontSize: '2.2rem', color: '#919eab', marginBottom: '-10px', marginLeft: '-7px' }}
        />
      ),
    },
    MedicaIdColumn,
    {
      headerColumn: 'patient',
      value: 'indexedGivenName',
      label: 'Name',
      icon: (
        <PersonIcon
          sx={{ marginRight: 1, color: '#919eab', marginBottom: '-3px' }}
          fontSize={'small'}
        />
      ),
    },
    {
      headerColumn: 'patient',
      value: 'indexedDob',
      label: 'Date of Birth',
      icon: (
        <CakeIcon
          sx={{ marginRight: 1, color: '#919eab', marginBottom: '-2px' }}
          fontSize={'small'}
        />
      ),
    },
    {
      headerColumn: 'contact',
      value: 'indexedPhone',
      label: 'Phone',
      icon: (
        <PhoneIcon
          sx={{ marginRight: 1, color: '#919eab', marginBottom: '-3px' }}
          fontSize={'small'}
        />
      ),
    },
    {
      headerColumn: 'contact',
      value: 'indexedEmail',
      label: 'Email',
      icon: (
        <EmailIcon
          sx={{ marginRight: 1, color: '#919eab', marginBottom: '-4px' }}
          fontSize={'small'}
        />
      ),
    },
    {
      headerColumn: 'dates',
      value: 'indexedCreatedAt',
      label: 'Registration Date',
      icon: (
        <TodayIcon
          sx={{ marginRight: 1, color: '#919eab', marginBottom: '-4px' }}
          fontSize={'small'}
        />
      ),
    },
    {
      headerColumn: 'dates',
      value: 'indexedMetaLastUpdatedOnUtc',
      label: 'Last Updated',
      icon: (
        <EventIcon
          sx={{ marginRight: 1, color: '#919eab', marginBottom: '-4px' }}
          fontSize={'small'}
        />
      ),
    },
  ];
};

export const initialServiceFilter = [
  {
    code: '1',
    display: 'Service Integration Level 1',
  },
  {
    code: '2',
    display: 'Service Integration Level 2',
  },
  {
    code: '3',
    display: 'Service Integration Level 3',
  },
];
export type UsersView = { code: string; label: string };

export interface GridState {
  page: number;
  rowsPerPage: number;
  orderBy: string | null;
  orderDirection: 'asc' | 'desc' | null;
  search: string;
  silFilter: ValueSetComposeIncludeConcept[];
  ownersToFilter: GeneralPractitionerGrid[];
  careTeamMembersToFilter: UsersView[];
  enrollment?: ValueSetComposeIncludeConcept | null | undefined;
  includeInactive: boolean;
  organizations?: { name: string; fhirId: string; }[];
  programType?: ValueSetComposeIncludeConcept | null | undefined;
}

interface PatientListProps {
  includeBreadcrumbs?: boolean;
  tableData: PaginatedPatientDto | null | undefined;
  setTableData: Dispatch<SetStateAction<PaginatedPatientDto | null | undefined>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  totalItems: number | null;
  setTotalItems: Dispatch<SetStateAction<number | null>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  initialGridState: GridState;
  ownersToFilter: GeneralPractitionerGrid[] | null;
  setOwnersToFilter: Dispatch<SetStateAction<GeneralPractitionerGrid[] | null>>;
  careTeamMembersToFilter: UsersView[] | null;
  setCareTeamMembersToFilter: Dispatch<SetStateAction<UsersView[] | null>>;
  includeStats?: boolean;
  setStats?: Dispatch<SetStateAction<any>>;
  show?: boolean;
  showFilters?: boolean;
  useOwnerFilter?: boolean;
  useCareTeamMemberFilter?: boolean;
  includeInactive?: boolean;
  onDeactivatePatient?: (patientId: string) => void;
  onActivatePatient?: (patientId: string) => void;
  isDeactivating?: boolean;
  isActivating?: boolean;
  refreshTrigger?: number;
}

export function PatientList({
  includeBreadcrumbs,
  tableData,
  setTableData,
  search,
  setSearch,
  totalItems,
  setTotalItems,
  initialGridState,
  isLoading,
  setIsLoading,
  ownersToFilter,
  setOwnersToFilter,
  careTeamMembersToFilter,
  setCareTeamMembersToFilter,
  includeStats = false,
  setStats,
  show = true,
  showFilters = true,
  useOwnerFilter = true,
  useCareTeamMemberFilter = false,
  includeInactive = false,
  onDeactivatePatient,
  onActivatePatient,
  isDeactivating = false,
  isActivating = false,
  refreshTrigger = 0
}: PatientListProps) {
  const { i18n } = useLocales();
  const {
    page,
    order,
    orderBy,
    rowsPerPage,
    onChangePage,
    onChangeRowsPerPage,
    setPage,
    onSort,
    setOrder,
  } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });
  const { componentsData } = useTenantConfigData();
  const { showSILInformation, showMedicaID, patient } = componentsData ?? {};
  const { list } = patient ?? {};
  const [gridState, updateGridState] = useObjectState<GridState>(initialGridState);

  const [silFilters, setSilFilters] = useState<ValueSetComposeIncludeConcept[]>();
  const [isOpenAplyFilters, setIsOpenAplyFilters] = useState<boolean>(false);
  const { showErrorMessage } = useNotiMessage();
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [generalPractitioners, setGeneralPractitioners] = useState<GeneralPractitionerGrid[]>([]);
  const [orgList, setOrgList] = useState<{ name: string, fhirId: string }[]>([]);

  const getPaginateQuery = (
    page: number,
    rowsPerPage: number,
    search: string,
    silFilters: any,
    orderBy: [string, string],
    ownersToFilter: GeneralPractitionerGrid[] | null,
    careTeamMembersToFilter: UsersView[] | null,
    useOwnerFilter: boolean,
    useCareTeamMemberFilter: boolean,
    includeInactive: boolean,
    includeStats?: boolean,
    enrollment?: ValueSetComposeIncludeConcept | null | undefined,
    organizations?: {name: string; fhirId: string; }[],
    programType?: ValueSetComposeIncludeConcept | null | undefined
  ) => {
    const paginateQuery: PaginateQueryWithStats = { page: page, limit: rowsPerPage };
    if (search) {
      paginateQuery.search = search;
    }
    paginateQuery.filter = {};

    if(!includeInactive){
      paginateQuery.filter.indexedStatus = "Active";
    }

    const [sortField, sortOrder] = orderBy;

    if (sortField && sortOrder) {
      paginateQuery.sortBy = [[sortField, sortOrder.toUpperCase()]];
    }

    if (silFilters?.length) {
      paginateQuery.filter.indexedCustomLastSil = `$in:${silFilters
        .map(({ code }: { code: any }) => code)
        .join(',')}`;
    }

    if (useOwnerFilter && ownersToFilter && ownersToFilter?.length > 0) {
      paginateQuery.filter.indexedGeneralPractitioners = `${ownersToFilter
        .map(({ reference }: GeneralPractitionerGrid) => reference)
        .join(',')}`;
    }

    if (useCareTeamMemberFilter && careTeamMembersToFilter && careTeamMembersToFilter.length) {
      paginateQuery.filter.indexedCareTeamsPractitioners = `${careTeamMembersToFilter
        .map(({ code }: { code: any }) => code)
        .join(',')}`;
    }

    if (includeStats) {
      paginateQuery.includeStats = true;
    }

    if (enrollment?.code) {
      paginateQuery.filter.enrollment = enrollment.code;
    } else if (paginateQuery.filter.enrollment) {
      delete paginateQuery.filter.enrollment;
    }

    if(organizations && organizations?.length > 0){
      paginateQuery.filter.indexedManagingOrgUuid = `$in:${organizations
        .map(({ fhirId }: { name: string, fhirId: string }) => fhirId)
        .join(',')}`;
    }

    if (programType?.code) {
      paginateQuery.filter.programType = programType.code;
    } else if (paginateQuery.filter.programType) {
      delete paginateQuery.filter.programType;
    }

    return paginateQuery;
  };

  const getGeneralPractitioners = useCallback(async () => {
    try {
      // setIsLoading(true);

      const prResponse = await fhirClient.search<PractitionerRole>(`PractitionerRole?active=true`);

      let practitionerRoles =
        prResponse?.entry?.reduce?.<WrappedPractitionerRole[]>((resources, { resource }) => {
          if (!resource) return resources;
          return [...resources, PractitionerRoleWrapper(resource)];
        }, []) ?? [];

      const [practitionerReferenceIds] = practitionerRoles.reduce<[string[]]>(
        ([practitionerIds], practitionerRole) => {
          const practitionerId = getReferenceId(practitionerRole?.practitioner?.reference);
          if (practitionerId) practitionerIds.push(practitionerId);
          return [practitionerIds];
        },
        [[]]
      );

      const allPractitionerIds = [...new Set([...practitionerReferenceIds])];

      const practitioneresponse = allPractitionerIds.length
        ? await fhirClient.search(
            `Practitioner?_id=${allPractitionerIds.join()}`
          )
        : null;

      const practitioners =
        practitioneresponse?.entry?.reduce?.<WrappedPractitioner[]>((resources, { resource }) => {
          if (!resource) return resources;
          return [...resources, PractitionerWrapper(resource as Practitioner)];
        }, []) ?? [];

      const generalPractitioners: GeneralPractitionerGrid[] = practitionerRoles
        .map((resource) => {
          const practitionerRole = resource as WrappedPractitionerRole;
          const practitioner = practitioners.find(
            ({ id }) => id === getReferenceId(practitionerRole?.practitioner?.reference)
          );

          return {
            id: practitionerRole.id,
            reference: `${practitionerRole?.resourceType}/${practitionerRole.id}`,
            role: practitionerRole?.code?.[0],
            roleActive: practitionerRole.active,
            ...(practitioner?.id && {
              practitioner: {
                id: practitioner?.id,
                fullName: practitioner?.getFullName?.(),
              },
            }),
          };
        })
        .filter((generalPractitioner) => !!generalPractitioner) as GeneralPractitionerGrid[];

      setGeneralPractitioners(generalPractitioners);
    } catch (error) {}
    // setIsLoading(false);
  }, []);

  useEffect(() => {
    getGeneralPractitioners();
  }, [getGeneralPractitioners]);

  async function loadOrganizations() {
    const organizations = getUserOrganizations();
    const oList: any = await getOrganizationsByIds(organizations);
    setOrgList(oList);
  }

  useEffect(() => {
    loadOrganizations();
  }, []);


  const fetchPatientList = async (
    page: number,
    rowsPerPage: number,
    search: string,
    silFilters: any,
    orderBy: [string, string],
    ownersToFilter: GeneralPractitionerGrid[] | null,
    careTeamMembersToFilter: UsersView[] | null,
    useOwnerFilter: boolean,
    useCareTeamMemberFilter: boolean,
    includeInactive: boolean,
    enrollment?: ValueSetComposeIncludeConcept | null | undefined,
    organizations?: { name: string; fhirId: string; }[],
    programType?: ValueSetComposeIncludeConcept | null | undefined
  ) => {

    setIsLoading(true);
    await wait();
    const paginateQuery = getPaginateQuery(
      page,
      rowsPerPage,
      search,
      silFilters,
      orderBy,
      ownersToFilter,
      careTeamMembersToFilter,
      useOwnerFilter,
      useCareTeamMemberFilter,
      includeInactive,
      includeStats,
      enrollment,
      organizations,
      programType
    );
    
    const patients = await PatientService.getAll(paginateQuery);


    if (!patients) {
      setIsLoading(false);
      return showErrorMessage(i18n('error.patient.list'));
    }

    if (patients?.meta?.totalItems !== undefined) {
      setTotalItems(patients?.meta?.totalItems);
    } else {
      setTotalItems(0);
    }

    setTableData(patients);

    if (includeStats && setStats && patients?.stats) {
      setStats(patients?.stats);
    }

    setIsLoading(false);
  };

  const handlerClear = () => {
    updateGridState({
      page: 1,
      rowsPerPage: 10,
      orderBy: null,
      orderDirection: null,
      search: '',
      silFilter: undefined,
      enrollment: null,
      programType: null,
      ownersToFilter: [],
      careTeamMembersToFilter: [],
      includeInactive: false,
      organizations: []
    });
    setOwnersToFilter(null);
    setCareTeamMembersToFilter(null);
    setSilFilters(undefined);
  };

  const getPatientList = useCallback(debounce(fetchPatientList, 600), []);

  const handlePageChange = (event: unknown, newPage: number) => {
    onChangePage(event, newPage + 1);
  };

  const handleSearch = (query: string) => {
    setSearch(query);
    setPage(1);
  };

  useEffect(() => {
    getPatientList(
      page,
      rowsPerPage,
      search,
      silFilters,
      [orderBy, order],
      includeStats ? ownersToFilter : gridState.ownersToFilter,
      includeStats ? careTeamMembersToFilter : gridState.careTeamMembersToFilter,
      useOwnerFilter,
      useCareTeamMemberFilter,
      includeStats ? includeInactive : gridState?.includeInactive ?? false,
      gridState.enrollment,
      gridState.organizations,
      gridState.programType
    );
  }, [
    page,
    rowsPerPage,
    search,
    order,
    orderBy,
    ownersToFilter,
    careTeamMembersToFilter,
    useOwnerFilter,
    useCareTeamMemberFilter,
    includeInactive,
    refreshTrigger
  ]);

  const patientDtoToPatientManager = (ref: PatientDto): PatientManager => {
    const upAtFormat = moment(ref.indexedUpdatedAt).format('MMM DD, YYYY');
    const lastUpdatedFormat = ref.metaLastUpdated
      ? moment(ref.metaLastUpdated).format('MMM DD, YYYY')
      : null;
    const row = {
      id: ref.indexedFhirRefUri?.slice(
        ref.indexedFhirRefUri?.lastIndexOf('/') + 1,
        ref.indexedFhirRefUri?.length
      ),
      mrn: ref.indexedMrn,
      referral: ref.indexedFullName,
      phone: ref.indexedPhone,
      email: ref.indexedEmail,
      birthDate: ref.indexedDob,
      gender: ref.indexedGender,
      demographics: ref.indexedEthnicity,
      race: ref.indexedRace,
      registrationDate: ref.indexedCreatedAt,
      formattedBirthDate: ref.indexedDOBFormatted,
      age: ref.indexedAge,
      registrationDateFormatted: ref.indexedCreatedAtFormatted,
      medicaId: ref.indexedCustomIdentifierMedicaidId,
      sil: ref.indexedCustomLastSil,
      createdAt: ref.indexedCreatedAtFormatted,
      updatedAt: upAtFormat,
      lastUpdated: lastUpdatedFormat,
      status: ref.indexedStatus,
      episodeOfCareData: ref.indexedCustomEpisodeOfCare,
      identifiers: identifierByConfig(ref.indexedIdentifierJson, list),
      managingOrg: ref.indexedManagingOrgDisplay,
    };
    return row;
  };

  const dataFormatted = (): PatientManager[] => {
    const data = tableData?.data ? tableData.data.map((r) => patientDtoToPatientManager(r)) : [];
    
    
    return data;
  };

  const rows = dataFormatted();

  const tableHeadPatientListFilter = TABLE_HEAD_PATIENT_LIST.filter((column) => {
    return showSILInformation || column.id !== 'indexedCustomLastSil';
  });

  const headLabel = translateTableHead(tableHeadPatientListFilter, i18n, 'crs');

  if (!show) {
    return null;
  }

  return (
    <>
      <PatientLayout
        title={i18n('patients.list.title', 'crs')}
        button={
          <>
            {checkAclValidation(
              { acls: [crsAcls.CRS.PATIENT.MANUAL_ADD] },
              ACLValidationMode.EXCLUSIVE
            ) && (
              <Grid item xs={12}>
                <Stack alignItems="flex-end">
                  <Button
                    variant="contained"
                    onClick={() => {
                      setIsAddPatientModalOpen(true);
                    }}
                  >
                    {i18n('patients.list.addPatient', 'crs')}
                  </Button>
                </Stack>
              </Grid>
            )}
          </>
        }
        includeBreadcrumbs={includeBreadcrumbs}
      >
        <>
          <Grid item xs={12}>
            <Card>
              <Stack direction="row" sx={{ py: 2, px: 2 }}>
                <Grid xs={6}>
                  <TableToolbar filterMrn={search} onFilterMrn={handleSearch} />
                </Grid>
                {showFilters ? (
                  <Grid container alignItems="center" justifyContent="flex-end" xs={6}>
                    <Tooltip title="Filter">
                      <IconButton
                        onClick={() => {
                          setIsOpenAplyFilters(true);
                        }}
                        sx={{ height: '40px' }}
                        disabled={isLoading}
                      >
                        <FilterListIcon htmlColor="#919eab" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ) : null}
              </Stack>
              <TableContainer>
                <Table>
                  <TableHeadCustom
                    headLabel={headLabel}
                    onSort={onSort}
                    orderBy={orderBy}
                    order={order}
                    headerCustomColumn={PatientHeaderColumn(!!showMedicaID)}
                    setOrder={setOrder}
                    hideSortIcon={false}
                  />
                  <TableBody>
                    {!isLoading ? (
                      rows?.length ? (
                        rows.map((row) => <PatientRow key={row.id} row={row} onDeactivate={onDeactivatePatient} onActivate={onActivatePatient} isDeactivating={isDeactivating} isActivating={isActivating} />)
                      ) : (
                        <NoResultsTableRow
                          colSpan={TABLE_HEAD_PATIENT_LIST.length}
                          text={i18n('patients.list.listNotFound', 'crs')}
                        />
                      )
                    ) : (
                      <LoadingTableRow colSpan={TABLE_HEAD_PATIENT_LIST.length} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid xs={12}>
                <Stack justifyContent="flex-end">
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    count={totalItems ? totalItems : 0}
                    rowsPerPage={rowsPerPage}
                    page={page - 1}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={onChangeRowsPerPage}
                    sx={{ borderTop: 0 }}
                  />
                </Stack>
              </Grid>
            </Card>
          </Grid>
          <FilterDrawer
            onApplyButtonClick={() => {
              getPatientList(
                page,
                rowsPerPage,
                search,
                gridState.silFilter,
                [order, orderBy],
                gridState.ownersToFilter,
                gridState.careTeamMembersToFilter,
                useOwnerFilter,
                useCareTeamMemberFilter,
                gridState.includeInactive,
                gridState.enrollment,
                gridState.organizations,
                gridState.programType
              );
              setIsOpenAplyFilters(false);
            }}
            title={i18n('patients.list.filterPopUp.title', 'crs')}
            onCloseIconButtonClick={() => {
              setIsOpenAplyFilters(false);
            }}
            onClearAllButtonClick={handlerClear}
            anchor={'right'}
            open={isOpenAplyFilters}
          >
            <PatientFiltersList
              gridState={gridState}
              updateGridState={updateGridState}
              setSilFilter={setSilFilters}
              setOwnersToFilter={setOwnersToFilter}
              patientSil={[]}
              generalPractitioners={generalPractitioners}
              i18n={i18n}
              includeStats={includeStats}
              orgList={orgList}
            />
          </FilterDrawer>
        </>
      </PatientLayout>
      <AddPatientModal
        open={isAddPatientModalOpen}
        onClose={() => {
          setIsAddPatientModalOpen(false);
        }}
      />
    </>
  );
}

interface PatientFilterListProps {
  gridState: GridState;
  updateGridState: any;
  setSilFilter: any;
  setOwnersToFilter: Dispatch<SetStateAction<GeneralPractitionerGrid[] | null>>;
  patientSil: any;
  generalPractitioners: GeneralPractitionerGrid[] | null;
  i18n: any;
  includeStats?: boolean;
  orgList: { name: string; fhirId: string; }[];
}

const PatientFiltersList = ({
  gridState,
  updateGridState,
  patientSil,
  setSilFilter,
  setOwnersToFilter,
  generalPractitioners,
  i18n,
  includeStats,
  orgList,
}: PatientFilterListProps) => {
  const { componentsData } = useTenantConfigData();
  const { showSILInformation } = componentsData ?? {};
  
  // Get Episode of Care Status valueset
  const [episodeOfCareStatusVS, { isLoading: isEpisodeOfCareStatusVSLoading }] = useValueSet(
    'episode-of-care-status',
    {
      map: ValueSetWrapper,
    }
  );

  const [episodeOfCareTypeVS, { isLoading: isEpisodeOfCareTypeVSLoading }] = useValueSet(
    'episodeofcare-type',
    {
      map: ValueSetWrapper,
    }
  );

  function getSelectedEnrollment() {
    if (!gridState.enrollment) {
      return null;
    }

    const enrollment = episodeOfCareStatusVS?.asListAll?.()?.find((item) => item.code === gridState.enrollment?.code);

    if (enrollment) {
      return enrollment;
    }

    return null;
  }

  const selectedEnrollment = useMemo(getSelectedEnrollment, [episodeOfCareStatusVS, gridState]);

  return (
    <Box py={3}>
      <Box>
        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            {i18n('patients.list.filterPopUp.owners', 'crs')}
          </Typography>
          <Autocomplete
            id="combo-box-user"
            value={gridState.ownersToFilter}
            fullWidth
            multiple
            onChange={(_: React.SyntheticEvent, generalPractitioner: any) => {
              if (includeStats) setOwnersToFilter(generalPractitioner);
              updateGridState((prevGridState: any) => ({
                ...prevGridState,
                ownersToFilter: generalPractitioner,
              }));
            }}
            options={generalPractitioners ?? []}
            getOptionLabel={(option) =>
              `${option?.practitioner?.fullName} | ${option?.role?.coding?.[0]?.display}`
            }
            renderInput={(params) => (
              <TextField
                label={i18n('patients.list.filterPopUp.ownersPlaceHolder', 'crs')}
                {...params}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option?.id}>
                {`${option?.practitioner?.fullName} | ${option?.role?.coding?.[0]?.display}`}
              </li>
            )}
          />
        </Grid>
        <br />
        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            {i18n('patients.list.filterPopUp.enrollment', 'crs')} Status

          </Typography>
          <Autocomplete
            id="combo-box-enrollment"
            value={selectedEnrollment}
            loading={isEpisodeOfCareStatusVSLoading}
            fullWidth
            onChange={(_: React.SyntheticEvent, value: ValueSetComposeIncludeConcept | null) => {
              updateGridState((prevGridState: any) => ({
                ...prevGridState,
                enrollment: value,
              }));
            }}
            options={episodeOfCareStatusVS?.asListAll?.() ?? []}
            getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
            renderInput={(params) => (
              <TextField
                label={i18n('patients.list.filterPopUp.enrollmentPlaceHolder', 'crs')}
                {...params}
              />
            )}
            renderOption={(props, option, index) => (
              <li {...props} key={index + option.code}>
                {`${option.display}`}
              </li>
            )}
          />
        </Grid>
        <br />
        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            {i18n('patients.list.filterPopUp.organizations', 'crs')}
          </Typography>
          <Autocomplete
            id="combo-box-organization"
            value={gridState.organizations ?? []}
            fullWidth
            disabled={!orgList?.length}
            multiple
            onChange={(_: React.SyntheticEvent, org: any) => {
              updateGridState((prevGridState: any) => ({
                ...prevGridState,
                organizations: org,
              }));
            }}
            options={orgList ?? []}
            getOptionLabel={({ name }: { name: string, fhirId: string }) => name ?? ''}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={i18n('patients.list.filterPopUp.organizations', 'crs')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {!orgList?.length ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <br />

        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            Enrollment Program
          </Typography>
          <Autocomplete
            id="combo-box-program-type"
            value={gridState.programType || null}
            loading={isEpisodeOfCareTypeVSLoading}
            fullWidth
            onChange={(_: React.SyntheticEvent, value: ValueSetComposeIncludeConcept | null) => {
              updateGridState((prevGridState: any) => ({
                ...prevGridState,
                programType: value,
              }));
            }}
            options={episodeOfCareTypeVS?.asListAll?.() ?? []}
            getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
            renderInput={(params) => (
              <TextField
                label="Select Program Type"
                placeholder="Filter by program type..."
                {...params}
              />
            )}
            renderOption={(props, option, index) => (
              <li {...props} key={index + option.code}>
                {`${option.display}`}
              </li>
            )}
            noOptionsText="No program types available"
            loadingText="Loading program types..."
          />
        </Grid>
        <br />

        {!!showSILInformation && (
          <Grid item sx={{ marginTop: 1 }} xs={12}>
            <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
              {i18n('patients.list.filterPopUp.sil', 'crs')}
            </Typography>
            <Autocomplete
              value={gridState.silFilter ?? []}
              multiple
              fullWidth
              onChange={(_: React.SyntheticEvent, silFilteres) => {
                setSilFilter(silFilteres);
                updateGridState((prevGridState: any) => ({
                  ...prevGridState,
                  silFilter: silFilteres,
                }));
              }}
              options={initialServiceFilter ?? []}
              getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n('patients.list.filterPopUp.silPlaceHolder', 'crs')}
                  variant="outlined"
                />
              )}
            />
          </Grid>
        )}

        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            {i18n('patients.list.filterPopUp.includeInactive', 'crs')}
          </Typography>
          <Switch
            name="includeInactive"
            onChange={(_, includeInactive) => {
              updateGridState((prevGridState: any) => ({
                ...prevGridState,
                includeInactive: includeInactive,
              }));
            }}
            checked={gridState.includeInactive}
          />
        </Grid>
      </Box>
    </Box>
  );
};
